import React from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Flex, Box, Heading, Text, Button, Center, VStack, Spinner, Icon, IconButton,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
} from "@chakra-ui/react";
import { FiPackage, FiX } from "react-icons/fi";

export function AdjustedQtyModal({
  isOpen,
  onClose,
  isLoading,
  error,
  data,
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      isCentered
      motionPreset="slideInBottom"
      scrollBehavior="inside"
      blockScrollOnMount={false}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent maxH="90vh" bg="white" borderRadius="2xl" boxShadow="2xl" overflow="hidden">
        <ModalHeader
          bgGradient="linear(to-r, red.600, red.500)"
          color="white" py={6} px={8}
          borderBottom="1px solid" borderColor="red.700"
        >
          <Flex align="center" justify="space-between">
            <Flex align="center" gap={3}>
              <Icon as={FiPackage} boxSize={6} />
              <Box>
                <Heading size="md" fontWeight="bold">Adjusted Quantity</Heading>
                <Text fontSize="sm" color="red.100" mt={1}>
                  Original vs adjusted stock quantities per product
                </Text>
              </Box>
            </Flex>
            <IconButton
              aria-label="Close modal"
              icon={<FiX />}
              variant="ghost"
              color="white"
              _hover={{ bg: "red.700" }}
              onClick={onClose}
              size="lg"
            />
          </Flex>
        </ModalHeader>

        <ModalBody px={8} py={6} overflowY="scroll">
          {isLoading ? (
            <Center py={12}>
              <VStack spacing={4}>
                <Spinner size="xl" color="red.500" thickness="4px" />
                <Text color="gray.600" fontWeight="medium">
                  Loading adjusted quantity data...
                </Text>
              </VStack>
            </Center>
          ) : error ? (
            <Center py={12}>
              <VStack spacing={3}>
                <Icon as={FiX} boxSize={12} color="red.400" />
                <Text color="red.600" fontWeight="semibold">Failed to load data</Text>
                <Text fontSize="sm" color="gray.500" textAlign="center">{error}</Text>
              </VStack>
            </Center>
          ) : data.length > 0 ? (
            <TableContainer>
              <Table variant="simple">
                <Thead bg="red.50">
                  <Tr>
                    <Th color="red.700" fontWeight="bold">Product Name</Th>
                    <Th color="red.700" fontWeight="bold" isNumeric>Pack Size</Th>
                    <Th color="red.700" fontWeight="bold">Brand Name</Th>
                    <Th color="red.700" fontWeight="bold" isNumeric>Original Qty</Th>
                    <Th color="red.700" fontWeight="bold" isNumeric>Adjusted Qty</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((item, idx) => (
                    <Tr key={idx} bg={idx % 2 === 0 ? "red.50" : "transparent"}>
                      <Td fontWeight="medium">{item.productName || "-"}</Td>
                      <Td isNumeric>{item.packsize ?? "-"}</Td>
                      <Td>{item.brandName || "-"}</Td>
                      <Td isNumeric>{item.originalQty ?? "-"}</Td>
                      <Td isNumeric fontWeight="semibold" color="red.600">
                        {item.adjustedQty ?? "-"}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <Center py={12}>
              <VStack spacing={3}>
                <Icon as={FiPackage} boxSize={12} color="gray.400" />
                <Text color="gray.600" fontWeight="medium">
                  No adjusted quantity data available
                </Text>
              </VStack>
            </Center>
          )}
        </ModalBody>

        <ModalFooter px={8} py={4} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
          <Flex w="100%" justify="space-between" align="center">
            <Text fontSize="sm" color="gray.600">
              {!isLoading && !error
                ? `${data.length} product${data.length !== 1 ? "s" : ""}`
                : ""}
            </Text>
            <Button
              variant="outline"
              colorScheme="gray"
              leftIcon={<FiX />}
              onClick={onClose}
              size="lg" px={8} borderRadius="lg"
            >
              Close
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
